/**
 * JS bridge for the native Android WalkieRecorder plugin.
 *
 * Why a custom plugin?
 *   The Capacitor Android WebView cancels long-press touch sequences
 *   (~150-200ms) regardless of any JS / CSS suppression. This makes
 *   WhatsApp-style press-and-hold voice recording impossible to
 *   implement reliably with WebView events. Our native plugin draws
 *   a transparent View on top of the JS button, receives Android
 *   MotionEvents directly, and uses native MediaRecorder.
 *
 * Platforms:
 *   - Android Capacitor native → uses this plugin
 *   - iOS Capacitor native     → keeps existing WebView-based logic (works)
 *   - PWA / browser            → keeps existing WebView-based logic (works)
 */
import { registerPlugin, Capacitor } from '@capacitor/core';

const WalkieRecorder = registerPlugin('WalkieRecorder');

export const isWalkieRecorderAvailable = () =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

/** Position the native overlay view on top of the JS button.
 *  Coords are in CSS pixels; the plugin converts to device px via density. */
export const showWalkieButton = async (rect) => {
  if (!isWalkieRecorderAvailable()) return;
  await WalkieRecorder.showButton({
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    dpr: window.devicePixelRatio || 1,
  });
};

export const checkWalkiePermissions = async () => {
  if (!isWalkieRecorderAvailable()) return { granted: true };
  return WalkieRecorder.checkMicPermission();
};

export const requestWalkiePermissions = async () => {
  if (!isWalkieRecorderAvailable()) return { granted: true };
  return WalkieRecorder.requestMicPermission();
};

export const onPermissionResult = (cb) =>
  WalkieRecorder.addListener('permissionResult', cb);

export const hideWalkieButton = async () => {
  if (!isWalkieRecorderAvailable()) return;
  await WalkieRecorder.hideButton();
};

/** Event subscriptions. Each returns a promise resolving to { remove: () => void }. */
export const onRecordingStarted = (cb) =>
  WalkieRecorder.addListener('recordingStarted', cb);
export const onRecordingStopped = (cb) =>
  WalkieRecorder.addListener('recordingStopped', cb);
export const onRecordingCancelled = (cb) =>
  WalkieRecorder.addListener('recordingCancelled', cb);
export const onRecordingError = (cb) =>
  WalkieRecorder.addListener('error', cb);

export default WalkieRecorder;
